import { z } from "zod";

// Letters a name written in the Latin alphabet can be made of: plain A-Z plus
// the accented ranges covering French, Spanish, Portuguese, German, Polish and
// Vietnamese (Latin-1 Supplement through Latin Extended Additional).
// The gaps skip the × (U+00D7) and ÷ (U+00F7) math symbols, which sit inside
// the Latin-1 Supplement block.
const NAME_LETTERS = "a-zA-ZÀ-ÖØ-öø-ɏḀ-ỿ";

// Space, hyphen, period and both the straight and the typographic apostrophe.
const NAME_SEPARATORS = " '’.-";

/**
 * A name has to start with a letter and can only continue with letters or the
 * separators above. Digits, braces, angle brackets and every other symbol are
 * rejected so template and script payloads — `{{7*7}}`, `<script>` — cannot be
 * submitted as a name.
 */
export const NAME_REGEX = new RegExp(
  `^[${NAME_LETTERS}][${NAME_LETTERS}${NAME_SEPARATORS}]*$`
);

export const NAME_MIN_LENGTH = 1;
export const NAME_MAX_LENGTH = 63;

export const nameSchema = z
  .string({ error: () => "form:error.required" })
  .trim()
  .min(NAME_MIN_LENGTH, { message: "form:error.required" })
  .max(NAME_MAX_LENGTH, { message: "form:error.tooLong" })
  .regex(NAME_REGEX, { message: "form:error.invalidName" });

export const gaqNameSchema = z
  .string({ error: () => "error:required" })
  .trim()
  .min(NAME_MIN_LENGTH, { message: "error:required" })
  .max(NAME_MAX_LENGTH, { message: "error:tooLong" })
  .regex(NAME_REGEX, { message: "error:invalidName" });

/** Optional name field: empty/whitespace is allowed; non-empty values must match NAME_REGEX. */
export const optionalNameSchema = z
  .string()
  .trim()
  .max(NAME_MAX_LENGTH, { message: "form:error.tooLong" })
  .refine((val) => val === "" || NAME_REGEX.test(val), {
    message: "form:error.invalidName",
  })
  .optional();
